import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const stats = [
  {
    value: '6',
    label: 'Trade documents reconciled per shipment',
    sub: 'Commercial Invoice → Packing List → Bill of Lading → CO → CoA → Organic Cert',
    pos: 'top-[10%] left-[6%]',
    delay: 0.05,
  },
  {
    value: '< 90s',
    label: 'Average pre-shipment review time',
    sub: 'From document upload to advisory report.',
    pos: 'top-[18%] right-[8%]',
    delay: 0.15,
  },
  {
    value: '0',
    label: 'Auto-submissions to customs',
    sub: 'AI interprets, rules decide, humans act.',
    pos: 'bottom-[24%] left-[10%]',
    delay: 0.25,
  },
  {
    value: '100%',
    label: 'Audit-ready validation history',
    sub: 'Every run captured with inputs, outputs, and source citations.',
    pos: 'bottom-[14%] right-[6%]',
    delay: 0.35,
  },
];

export default function ImpactQuantified() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  // Parallax: huge word translates slowly upward as we scroll past the section
  const y = useTransform(scrollYProgress, [0, 1], [120, -120]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.96, 1, 1.02]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.85, 1], [0, 1, 1, 0.5]);

  return (
    <section ref={ref} className="relative py-32 md:py-48 overflow-hidden">
      {/* Backdrop ambient gradient */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-glow/[0.03] to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-2"
        >
          <span className="text-xs uppercase tracking-[0.4em] text-cyan-glow font-mono">Key metrics</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-4"
        >
          <h2 className="font-display text-2xl md:text-3xl text-slate-300 tracking-tight">
            The impact,
          </h2>
        </motion.div>

        {/* Pinned-feel monumental typography with parallax */}
        <div className="relative h-[420px] md:h-[560px]">
          <motion.div
            style={{ y, scale, opacity }}
            className="absolute inset-x-0 top-0 flex items-center justify-center pointer-events-none select-none"
          >
            <span
              className="font-display font-black text-[24vw] md:text-[20vw] leading-none tracking-tighter bg-gradient-to-b from-white via-cyan-glow/80 to-cyan-glow/20 bg-clip-text text-transparent"
              data-testid="quantified-headline"
            >
              Quantified
            </span>
          </motion.div>

          {/* Floating stat cards in front of the giant word */}
          {stats.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30, scale: 0.96 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.7, delay: s.delay, ease: [0.16, 1, 0.3, 1] }}
              className={`absolute ${s.pos} max-w-[260px] md:max-w-[300px]`}
              data-testid={`impact-stat-${i}`}
            >
              <div className="rounded-xl border border-white/10 bg-navy-900/80 backdrop-blur-xl p-5 shadow-2xl shadow-cyan-glow/5 hover:border-cyan-glow/30 transition-colors">
                <div className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight">{s.value}</div>
                <div className="text-sm font-medium text-slate-200 mt-2">{s.label}</div>
                <div className="text-xs text-slate-500 mt-1.5 leading-relaxed">{s.sub}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
